import { useState, useCallback } from 'react';
import {
  FileText,
  Plus,
  Trash2,
  Loader2,
  GripVertical,
  Download,
  Copy,
  Check,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { exportReportToPDF, exportReportToDOCX } from '@/features/export/report-export';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useFolderTree } from '@/features/sources/hooks/useFolders';
import { toast } from 'sonner';

// =========================================
// Types
// =========================================

type SectionType =
  | 'executive_summary'
  | 'methodology'
  | 'inclusion_criteria'
  | 'results_overview'
  | 'detailed_findings'
  | 'quality_assessment'
  | 'limitations'
  | 'conclusions'
  | 'custom';

interface ReportSectionConfig {
  id: string;
  type: SectionType;
  title: string;
  instructions: string;
  enabled: boolean;
}

interface GeneratedSection {
  type: string;
  title: string;
  content: string;
}

interface ReportOutput {
  title: string;
  generated_at: string;
  sections: GeneratedSection[];
  source_count: number;
  included_count: number;
  excluded_count: number;
}

interface ReportBuilderProps {
  /** Pre-select a folder */
  initialFolderId?: string;
  className?: string;
}

// =========================================
// Constants
// =========================================

const SECTION_PRESETS: Array<{
  type: SectionType;
  title: string;
  description: string;
}> = [
  {
    type: 'executive_summary',
    title: 'Executive Summary',
    description: 'Brief overview of purpose, methods, key findings, and conclusions',
  },
  {
    type: 'methodology',
    title: 'Methodology',
    description: 'Systematic review methodology: search strategy, screening, AI scoring',
  },
  {
    type: 'inclusion_criteria',
    title: 'Inclusion Criteria',
    description: 'Criteria used for inclusion/exclusion and how they were applied',
  },
  {
    type: 'results_overview',
    title: 'Results Overview',
    description: 'Quantitative results: sources per bucket, score distribution, patterns',
  },
  {
    type: 'detailed_findings',
    title: 'Detailed Findings',
    description: 'Synthesized findings across included sources, themes, and contradictions',
  },
  {
    type: 'quality_assessment',
    title: 'Quality Assessment',
    description: 'Overall quality of evidence, biases, and methodological concerns',
  },
  {
    type: 'limitations',
    title: 'Limitations',
    description: 'Review limitations: search coverage, AI evaluation, potential biases',
  },
  {
    type: 'conclusions',
    title: 'Conclusions',
    description: 'Key conclusions, implications, and future research directions',
  },
];

function createId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function buildDefaultSections(): ReportSectionConfig[] {
  return SECTION_PRESETS.map((preset) => ({
    id: createId(),
    type: preset.type,
    title: preset.title,
    instructions: '',
    enabled: true,
  }));
}

function flattenFolderTree(
  nodes: Array<{ id: string; name: string; children: Array<unknown>; source_count: number }>,
  depth = 0
): Array<{ id: string; name: string; depth: number; source_count: number }> {
  const result: Array<{ id: string; name: string; depth: number; source_count: number }> = [];
  for (const node of nodes) {
    result.push({ id: node.id, name: node.name, depth, source_count: node.source_count });
    const children = node.children as typeof nodes;
    if (children?.length) {
      result.push(...flattenFolderTree(children, depth + 1));
    }
  }
  return result;
}

// =========================================
// Safe markdown renderer (React elements, no innerHTML)
// =========================================

function MarkdownContent({ content }: { content: string }) {
  const blocks = content.split('\n\n').filter((b) => b.trim());

  return (
    <div className="space-y-3">
      {blocks.map((block, i) => {
        const trimmed = block.trim();

        // Heading
        if (trimmed.startsWith('#### ')) {
          return (
            <h4 key={i} className="text-sm font-semibold text-slate-800 mt-4">
              {renderInline(trimmed.slice(5))}
            </h4>
          );
        }
        if (trimmed.startsWith('### ')) {
          return (
            <h4 key={i} className="text-sm font-semibold text-slate-800 mt-4">
              {renderInline(trimmed.slice(4))}
            </h4>
          );
        }
        if (trimmed.startsWith('## ')) {
          return (
            <h3 key={i} className="text-base font-semibold text-slate-800 mt-4">
              {renderInline(trimmed.slice(3))}
            </h3>
          );
        }

        // Unordered list
        const ulLines = trimmed.split('\n').filter((l) => l.match(/^[-*] /));
        if (ulLines.length > 0 && ulLines.length === trimmed.split('\n').filter(Boolean).length) {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1">
              {ulLines.map((line, j) => (
                <li key={j} className="text-sm text-slate-600">
                  {renderInline(line.replace(/^[-*] /, ''))}
                </li>
              ))}
            </ul>
          );
        }

        // Ordered list
        const olLines = trimmed.split('\n').filter((l) => l.match(/^\d+\. /));
        if (olLines.length > 0 && olLines.length === trimmed.split('\n').filter(Boolean).length) {
          return (
            <ol key={i} className="list-decimal pl-5 space-y-1">
              {olLines.map((line, j) => (
                <li key={j} className="text-sm text-slate-600">
                  {renderInline(line.replace(/^\d+\. /, ''))}
                </li>
              ))}
            </ol>
          );
        }

        // Paragraph
        return (
          <p key={i} className="text-sm text-slate-600 leading-relaxed">
            {renderInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
}

/** Render inline markdown (bold, italic) as text-only (no HTML injection) */
function renderInline(text: string): string {
  return text
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1');
}

// =========================================
// ReportBuilder
// =========================================

export function ReportBuilder({ initialFolderId, className }: ReportBuilderProps) {
  const { data: folderTree } = useFolderTree();

  // Form state
  const [title, setTitle] = useState('Systematic Review Report');
  const [folderId, setFolderId] = useState(initialFolderId ?? '');
  const [aiModel, setAiModel] = useState<'gpt-4-turbo' | 'claude-3.5-sonnet'>('gpt-4-turbo');
  const [sections, setSections] = useState<ReportSectionConfig[]>(buildDefaultSections);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState<ReportOutput | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const flatFolders = flattenFolderTree(
    (folderTree ?? []) as Array<{
      id: string;
      name: string;
      children: Array<unknown>;
      source_count: number;
    }>
  );

  const enabledSections = sections.filter((s) => s.enabled);

  // Section management
  const toggleSection = useCallback((id: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s))
    );
  }, []);

  const updateSectionTitle = useCallback((id: string, newTitle: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, title: newTitle } : s))
    );
  }, []);

  const updateSectionInstructions = useCallback((id: string, instructions: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, instructions } : s))
    );
  }, []);

  const removeSection = useCallback((id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const addCustomSection = useCallback(() => {
    setSections((prev) => [
      ...prev,
      {
        id: createId(),
        type: 'custom' as SectionType,
        title: 'Custom Section',
        instructions: '',
        enabled: true,
      },
    ]);
  }, []);

  const moveSection = useCallback((id: string, direction: 'up' | 'down') => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx === -1) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const updated = [...prev];
      const temp = updated[idx]!;
      updated[idx] = updated[newIdx]!;
      updated[newIdx] = temp;
      return updated;
    });
  }, []);

  // Generate report
  const handleGenerate = useCallback(async () => {
    if (!folderId) {
      toast.error('Please select a folder');
      return;
    }
    if (enabledSections.length === 0) {
      toast.error('Please enable at least one section');
      return;
    }

    setIsGenerating(true);
    setReport(null);

    try {
      const data = await api.generateReport({
        folder_id: folderId,
        title,
        sections: enabledSections.map((s) => ({
          type: s.type,
          title: s.title,
          instructions: s.instructions || undefined,
        })),
        ai_model: aiModel,
      }) as ReportOutput;
      setReport(data);
      toast.success('Report generated successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Report generation failed: ${message}`);
    } finally {
      setIsGenerating(false);
    }
  }, [folderId, title, enabledSections, aiModel]);

  // Export as markdown
  const reportMarkdown = report
    ? [
        `# ${report.title}`,
        '',
        `*Generated: ${new Date(report.generated_at).toLocaleDateString()}*`,
        `*Sources: ${report.source_count} total | ${report.included_count} included | ${report.excluded_count} excluded*`,
        '',
        '---',
        '',
        ...report.sections.flatMap((s) => [`## ${s.title}`, '', s.content, '', '---', '']),
      ].join('\n')
    : '';

  const handleCopyMarkdown = useCallback(() => {
    navigator.clipboard.writeText(reportMarkdown);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  }, [reportMarkdown]);

  const handleDownloadMarkdown = useCallback(() => {
    const blob = new Blob([reportMarkdown], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/\s+/g, '_').toLowerCase()}.md`;
    link.click();
    URL.revokeObjectURL(url);
  }, [reportMarkdown, title]);

  return (
    <div className={cn('grid grid-cols-1 lg:grid-cols-2 gap-6', className)}>
      {/* Left: Configuration */}
      <div className="space-y-6">
        {/* Report settings */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-indigo-500" />
              <CardTitle className="text-base">Report Settings</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Report Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Systematic Review Report"
                className="h-9"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Source Folder</Label>
              <Select value={folderId} onValueChange={setFolderId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select a folder..." />
                </SelectTrigger>
                <SelectContent>
                  {flatFolders.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      <span style={{ paddingLeft: `${f.depth * 16}px` }}>
                        {f.name}
                        <span className="text-xs text-slate-400 ml-2">
                          ({f.source_count} sources)
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">AI Model</Label>
              <Select
                value={aiModel}
                onValueChange={(v) => setAiModel(v as typeof aiModel)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                  <SelectItem value="claude-3.5-sonnet">Claude 3.5 Sonnet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-slate-500">
                Cost: <Badge variant="secondary" className="text-xs">2 credits</Badge>
              </span>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !folderId || enabledSections.length === 0}
                className="gap-2"
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section picker */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Sections
                <Badge variant="outline" className="ml-2 text-xs">
                  {enabledSections.length} / {sections.length}
                </Badge>
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={addCustomSection}
              >
                <Plus className="h-3 w-3 mr-1" />
                Custom
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {sections.map((section, idx) => {
              const preset = SECTION_PRESETS.find((p) => p.type === section.type);
              const isExpanded = expandedSection === section.id;

              return (
                <div
                  key={section.id}
                  className={cn(
                    'border rounded-lg p-3 transition-colors',
                    section.enabled
                      ? 'border-slate-200 bg-white'
                      : 'border-slate-100 bg-slate-50 opacity-60'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />

                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700 truncate">
                          {section.title}
                        </span>
                        {section.type === 'custom' && (
                          <Badge className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700 border-0">
                            Custom
                          </Badge>
                        )}
                      </div>
                      {preset && (
                        <span className="text-[11px] text-slate-400 truncate">
                          {preset.description}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => moveSection(section.id, 'up')}
                        disabled={idx === 0}
                      >
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => moveSection(section.id, 'down')}
                        disabled={idx === sections.length - 1}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() =>
                          setExpandedSection(isExpanded ? null : section.id)
                        }
                      >
                        <FileText className="h-3 w-3" />
                      </Button>
                      {section.type === 'custom' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-slate-400 hover:text-rose-500"
                          onClick={() => removeSection(section.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                      <Switch
                        checked={section.enabled}
                        onCheckedChange={() => toggleSection(section.id)}
                        className="scale-75"
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-3 space-y-2 pt-2 border-t border-slate-100">
                      {section.type === 'custom' && (
                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">
                            Section Title
                          </Label>
                          <Input
                            value={section.title}
                            onChange={(e) =>
                              updateSectionTitle(section.id, e.target.value)
                            }
                            className="h-8 text-xs"
                            placeholder="Section title..."
                          />
                        </div>
                      )}
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">
                          Additional Instructions (optional)
                        </Label>
                        <Input
                          value={section.instructions}
                          onChange={(e) =>
                            updateSectionInstructions(section.id, e.target.value)
                          }
                          className="h-8 text-xs"
                          placeholder="e.g. Focus on RCTs only..."
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Right: Preview */}
      <Card className="h-fit">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Report Preview</CardTitle>
              {report && (
                <Badge variant="secondary" className="text-xs">
                  {report.sections.length} sections
                </Badge>
              )}
            </div>
            {report && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={handleCopyMarkdown}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 mr-1" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 mr-1" />
                  )}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={handleDownloadMarkdown}
                >
                  <Download className="h-3.5 w-3.5 mr-1" />
                  .md
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => report && exportReportToPDF(report)}
                >
                  <Download className="h-3.5 w-3.5 mr-1" />
                  .pdf
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => report && exportReportToDOCX(report)}
                >
                  <Download className="h-3.5 w-3.5 mr-1" />
                  .docx
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isGenerating && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-8 w-8 animate-spin mb-3" />
              <p className="text-sm font-medium">Generating report...</p>
              <p className="text-xs mt-1">This may take a minute</p>
            </div>
          )}

          {!isGenerating && !report && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-300">
              <FileText className="h-12 w-12 mb-3" />
              <p className="text-sm text-slate-400">
                Configure sections and click Generate
              </p>
            </div>
          )}

          {!isGenerating && report && (
            <div className="space-y-6 max-h-[700px] overflow-auto pr-2">
              {/* Header */}
              <div>
                <h2 className="text-xl font-bold text-slate-900">{report.title}</h2>
                <p className="text-xs text-slate-400 mt-1">
                  Generated {new Date(report.generated_at).toLocaleDateString()}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    {report.source_count} sources
                  </Badge>
                  <Badge className="text-xs bg-emerald-100 text-emerald-700 border-0">
                    {report.included_count} included
                  </Badge>
                  <Badge className="text-xs bg-rose-100 text-rose-700 border-0">
                    {report.excluded_count} excluded
                  </Badge>
                </div>
              </div>

              <Separator />

              {/* Sections */}
              {report.sections.map((section, idx) => (
                <div key={idx}>
                  <h3 className="text-base font-semibold text-slate-800 mb-2">
                    {section.title}
                  </h3>
                  <MarkdownContent content={section.content} />
                  {idx < report.sections.length - 1 && <Separator className="mt-6" />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
